import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SearchableSelect } from "../ui/searchable-select";
import { toast } from "sonner";

const CONVERSION_METHODS = [
  { value: "units_per_sf", label: "Units per SF" },
  { value: "units_per_lf", label: "Units per LF" },
  { value: "by_sf", label: "Order by SF" },
  { value: "by_lf", label: "Order by LF" },
  { value: "per_piece", label: "Per Piece" },
];

const MEASURE_MODES = [
  { value: "area", label: "Area (L × W)" },
  { value: "linear", label: "Linear (L only)" },
];

const TakeOffTab = ({ job, materials = [], onSaveTakeoff, onHasChangesChange }) => {
  const [columns, setColumns] = useState(() => {
    const saved = job.takeoff?.columns;
    if (saved && saved.length > 0) return saved;
    return [createEmptyColumn()];
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastInputRef = useRef(null);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    if (onHasChangesChange) onHasChangesChange(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  // Auto-save with 1.5s debounce
  useEffect(() => {
    if (!hasChanges) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await onSaveTakeoff({ columns });
        setHasChanges(false);
      } catch {
        // Silent fail for auto-save; user can still manually save
      }
      setSaving(false);
    }, 1500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [columns, hasChanges, onSaveTakeoff]);

  function createEmptyColumn(materialId) {
    return {
      id: crypto.randomUUID(),
      material_id: materialId || "",
      material_name: "",
      measure_mode: "area",
      conversion_method: "units_per_sf",
      conversion_factor: "",
      notes: "",
      rows: [createEmptyRow()],
    };
  }

  function createEmptyRow() {
    return { id: crypto.randomUUID(), length: "", width: "" };
  }

  const materialOptions = useMemo(() =>
    materials
      .map(m => ({ value: m.id, label: m.name, unit: m.unit, category: m.category }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [materials]
  );

  const updateColumn = useCallback((colId, updates) => {
    setColumns(prev => prev.map(c => c.id === colId ? { ...c, ...updates } : c));
    setHasChanges(true);
  }, []);

  const updateRow = useCallback((colId, rowId, field, value) => {
    setColumns(prev => prev.map(c => {
      if (c.id !== colId) return c;
      return {
        ...c,
        rows: c.rows.map(r => r.id === rowId ? { ...r, [field]: value } : r),
      };
    }));
    setHasChanges(true);
  }, []);

  const handleRowKeyDown = useCallback((e, colId, rowId, field, isLinear) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const isLastField = isLinear ? field === "length" : field === "width";
      
      if (isLastField) {
        // On the last field of a row — add new row if this is the last one, then focus it
        let newRowId = null;
        setColumns(prev => prev.map(c => {
          if (c.id !== colId) return c;
          const rowIndex = c.rows.findIndex(r => r.id === rowId);
          const isLast = rowIndex === c.rows.length - 1;
          const currentRow = c.rows[rowIndex];
          if (isLast && (currentRow.length || currentRow.width)) {
            const newRow = createEmptyRow();
            newRowId = newRow.id;
            return { ...c, rows: [...c.rows, newRow] };
          }
          // If not last row, focus next row's length
          if (!isLast) {
            newRowId = c.rows[rowIndex + 1].id;
          }
          return c;
        }));
        setHasChanges(true);
        // Focus the new/next row's length input after render
        setTimeout(() => {
          if (newRowId) {
            const nextInput = document.querySelector(`[data-testid="row-length-${newRowId}"]`);
            if (nextInput) nextInput.focus();
          }
        }, 50);
      } else if (field === "length" && !isLinear) {
        // In area mode, Enter on length → move to width in same row
        const widthInput = document.querySelector(`[data-testid="row-width-${rowId}"]`);
        if (widthInput) widthInput.focus();
      }
    }
  }, []);

  const deleteRow = useCallback((colId, rowId) => {
    setColumns(prev => prev.map(c => {
      if (c.id !== colId) return c;
      const filtered = c.rows.filter(r => r.id !== rowId);
      return { ...c, rows: filtered.length === 0 ? [createEmptyRow()] : filtered };
    }));
    setHasChanges(true);
  }, []);

  const addColumn = useCallback(() => {
    setColumns(prev => [...prev, createEmptyColumn()]);
    setHasChanges(true);
  }, []);

  const deleteColumn = useCallback((colId) => {
    setColumns(prev => {
      const filtered = prev.filter(c => c.id !== colId);
      return filtered.length === 0 ? [createEmptyColumn()] : filtered;
    });
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    clearTimeout(autoSaveTimer.current);
    setSaving(true);
    try {
      await onSaveTakeoff({ columns });
      setHasChanges(false);
      toast.success("Take Off saved");
    } catch {
      toast.error("Failed to save Take Off");
    }
    setSaving(false);
  }, [columns, onSaveTakeoff]);

  const calcSF = (row) => {
    const l = parseFloat(row.length) || 0;
    const w = parseFloat(row.width) || 0;
    return l * w;
  };

  const calcTotalSF = (col) =>
    col.rows.reduce((sum, r) => sum + calcSF(r), 0);

  const calcTotalLength = (col) =>
    col.rows.reduce((sum, r) => sum + (parseFloat(r.length) || 0), 0);

  const calcTotalWidth = (col) =>
    col.rows.reduce((sum, r) => sum + (parseFloat(r.width) || 0), 0);

  const calcTotalMeasure = (col) =>
    col.measure_mode === "linear" ? calcTotalLength(col) : calcTotalSF(col);

  const calcTotalMaterial = (col) => {
    const total = calcTotalMeasure(col);
    const factor = parseFloat(col.conversion_factor) || 0;
    switch (col.conversion_method) {
      case "units_per_sf":
      case "units_per_lf":
        return total * factor;
      case "by_sf":
      case "by_lf":
        return total;
      case "per_piece":
        return col.rows.filter(r => (parseFloat(r.length) || 0) > 0).length;
      default:
        return total;
    }
  };

  const getConversionMethods = (mode) => {
    if (mode === "linear") {
      return CONVERSION_METHODS.filter(m => ["units_per_lf", "by_lf", "per_piece"].includes(m.value));
    }
    return CONVERSION_METHODS.filter(m => ["units_per_sf", "by_sf", "per_piece"].includes(m.value));
  };

  const fmt = (n) => {
    if (n === 0) return "0";
    return n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Material Take Off</h3>
          {saving ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
              Saving...
            </span>
          ) : hasChanges ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
              Unsaved changes
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={addColumn}
            data-testid="add-material-column-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Material
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            data-testid="save-takeoff-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Columns - responsive grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {columns.map((col) => {
          const isLinear = col.measure_mode === "linear";
          const totalMeasure = calcTotalMeasure(col);
          const totalMaterial = calcTotalMaterial(col);
          const materialInfo = materials.find(m => m.id === col.material_id);

          return (
            <Card
              key={col.id}
              className="flex flex-col"
              data-testid={`takeoff-column-${col.id}`}
            >
              <CardContent className="p-4 space-y-3 flex flex-col">
                {/* Material selector */}
                <div className="flex items-center gap-2">
                  <SearchableSelect
                    value={col.material_id}
                    onValueChange={(val) => {
                      const mat = materials.find(m => m.id === val);
                      updateColumn(col.id, {
                        material_id: val,
                        material_name: mat?.name || "",
                      });
                    }}
                    placeholder="Select Material"
                    searchPlaceholder="Search materials..."
                    options={materialOptions.map(opt => ({
                      value: opt.value,
                      label: opt.label,
                      subtitle: opt.category ? `${opt.category} · Unit: ${opt.unit}` : `Unit: ${opt.unit}`,
                    }))}
                    triggerClassName="flex-1 font-semibold"
                    data-testid={`material-select-${col.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteColumn(col.id)}
                    data-testid={`delete-column-${col.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Measure mode toggle */}
                <div className="flex gap-1 bg-muted/50 rounded-md p-0.5">
                  {MEASURE_MODES.map(m => (
                    <button
                      key={m.value}
                      className={`flex-1 text-xs py-1 px-2 rounded transition-colors ${
                        col.measure_mode === m.value
                          ? "bg-background shadow-sm font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => {
                        const newMethod = m.value === "linear" ? "units_per_lf" : "units_per_sf";
                        updateColumn(col.id, { measure_mode: m.value, conversion_method: newMethod });
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {/* Column headers */}
                {isLinear ? (
                  <div className="grid grid-cols-[1fr_32px] gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
                    <span>Length</span>
                    <span></span>
                  </div>
                ) : (
                  <div className="grid grid-cols-[1fr_16px_1fr_80px_32px] gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
                    <span>Length</span>
                    <span></span>
                    <span>Width</span>
                    <span className="text-right">SF</span>
                    <span></span>
                  </div>
                )}

                {/* Measurement rows - fixed height for 5 rows, scrollable */}
                <div className="space-y-1 h-[200px] overflow-y-auto">
                  {col.rows.map((row) => {
                    const sf = calcSF(row);
                    return isLinear ? (
                      <div
                        key={row.id}
                        className="grid grid-cols-[1fr_32px] gap-1 items-center"
                        data-testid={`takeoff-row-${row.id}`}
                      >
                        <Input
                          type="number"
                          step="any"
                          placeholder="Length"
                          value={row.length}
                          onChange={(e) => updateRow(col.id, row.id, "length", e.target.value)}
                          onKeyDown={(e) => handleRowKeyDown(e, col.id, row.id, "length", true)}
                          className="h-8 text-sm font-mono takeoff-input"
                          data-testid={`row-length-${row.id}`}
                        />
                        {col.rows.length > 1 ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => deleteRow(col.id, row.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        ) : <span />}
                      </div>
                    ) : (
                      <div
                        key={row.id}
                        className="grid grid-cols-[1fr_16px_1fr_80px_32px] gap-1 items-center"
                        data-testid={`takeoff-row-${row.id}`}
                      >
                        <Input
                          type="number"
                          step="any"
                          placeholder="L"
                          value={row.length}
                          onChange={(e) => updateRow(col.id, row.id, "length", e.target.value)}
                          onKeyDown={(e) => handleRowKeyDown(e, col.id, row.id, "length", false)}
                          className="h-8 text-sm font-mono takeoff-input"
                          data-testid={`row-length-${row.id}`}
                        />
                        <span className="text-center text-muted-foreground text-xs font-medium">×</span>
                        <Input
                          type="number"
                          step="any"
                          placeholder="W"
                          value={row.width}
                          onChange={(e) => updateRow(col.id, row.id, "width", e.target.value)}
                          onKeyDown={(e) => handleRowKeyDown(e, col.id, row.id, "width", false)}
                          className="h-8 text-sm font-mono takeoff-input"
                          data-testid={`row-width-${row.id}`}
                        />
                        <span className="text-right text-sm font-mono pr-1">
                          {sf > 0 ? fmt(sf) : ""}
                        </span>
                        {col.rows.length > 1 ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => deleteRow(col.id, row.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        ) : <span />}
                      </div>
                    );
                  })}
                </div>

                {/* Column totals row */}
                <div className="border-t pt-2">
                  {isLinear ? (
                    <div className="grid grid-cols-[1fr_32px] gap-1 px-1">
                      <div className="text-right text-sm font-semibold font-mono pr-1">
                        {fmt(calcTotalLength(col))} LF
                      </div>
                      <span />
                    </div>
                  ) : (
                    <div className="grid grid-cols-[1fr_16px_1fr_80px_32px] gap-1 px-1">
                      <div className="text-sm font-semibold font-mono text-center">
                        {fmt(calcTotalLength(col))}
                      </div>
                      <span></span>
                      <div className="text-sm font-semibold font-mono text-center">
                        {fmt(calcTotalWidth(col))}
                      </div>
                      <div className="text-right text-sm font-semibold font-mono pr-1">
                        {fmt(totalMeasure)}
                      </div>
                      <span />
                    </div>
                  )}
                </div>

                {/* Totals section */}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span>{isLinear ? "Total LF" : "Total SF"}</span>
                    <span className="font-mono text-base" data-testid={`total-sf-${col.id}`}>
                      {fmt(totalMeasure)}
                    </span>
                  </div>

                  {/* Conversion method */}
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-medium">Conversion</label>
                    <Select
                      value={col.conversion_method}
                      onValueChange={(val) => updateColumn(col.id, { conversion_method: val })}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`conversion-method-${col.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getConversionMethods(col.measure_mode).map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Factor input (only for units_per_sf/lf) */}
                  {(col.conversion_method === "units_per_sf" || col.conversion_method === "units_per_lf") && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground whitespace-nowrap">Factor:</label>
                      <Input
                        type="number"
                        step="any"
                        placeholder="Enter factor"
                        value={col.conversion_factor}
                        onChange={(e) => updateColumn(col.id, { conversion_factor: e.target.value })}
                        className="h-8 text-sm font-mono w-24 takeoff-input"
                        data-testid={`conversion-factor-${col.id}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        {materialInfo?.unit || "units"}/{isLinear ? "LF" : "SF"}
                      </span>
                    </div>
                  )}

                  {/* Notes field */}
                  <Input
                    placeholder="Notes (e.g. color, spec...)"
                    value={col.notes || ""}
                    onChange={(e) => updateColumn(col.id, { notes: e.target.value })}
                    className="h-8 text-sm"
                    data-testid={`notes-${col.id}`}
                  />

                  {/* Total Material */}
                  <div className="flex justify-between items-center bg-muted/50 rounded-md px-3 py-2">
                    <span className="text-sm font-semibold">
                      {col.conversion_method === "per_piece" ? "Total Pieces" : 
                       col.conversion_method === "by_sf" ? "Total SF" :
                       col.conversion_method === "by_lf" ? "Total LF" :
                       `Total ${materialInfo?.unit || "Units"}`}
                    </span>
                    <span className="font-mono text-lg font-bold" data-testid={`total-material-${col.id}`}>
                      {fmt(Math.ceil(totalMaterial))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TakeOffTab;
